// ========================================
// PAGE PROJECTS - VERSION OPTIMISÉE
// Liste des projets avec animations légères
// ========================================

import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../../components/common/Navigation';
import Footer from '../../components/common/Footer';
import LoadingScreen from '../../components/common/LoadingScreen';
import { useProjects } from '../../hooks';
import { 
  FiSearch, 
  FiCalendar,
  FiArrowRight,
  FiX,
  FiFolder,
  FiLayers,
  FiAward,
  FiTrendingUp
} from 'react-icons/fi';
import './Projects.css';

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
const Projects = () => {
  const { projects, loading } = useProjects();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isPageReady, setIsPageReady] = useState(false);

  // Marquer la page comme prête après le chargement initial
  useEffect(() => {
    if (!loading && projects?.length) {
      // Petit délai pour laisser le DOM se stabiliser
      const timer = setTimeout(() => setIsPageReady(true), 100);
      return () => clearTimeout(timer);
    }
  }, [loading, projects]);

  // ========== CATÉGORIES DYNAMIQUES ==========
  const categories = useMemo(() => {
    if (!projects?.length) return [{ id: 'all', label: 'Tous', count: 0, icon: FiLayers }];

    const categoryCounts = projects.reduce((acc, project) => {
      const cat = project.category || 'autre';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});

    const categoryConfig = {
      all: { label: 'Tous les projets', icon: FiLayers },
      ong: { label: 'ONG', icon: FiAward },
      association: { label: 'Associations', icon: FiTrendingUp },
      agence: { label: 'Agences', icon: FiFolder },
      media: { label: 'Médias', icon: FiFolder },
      entreprise: { label: 'Entreprises', icon: FiFolder },
      personnel: { label: 'Personnel', icon: FiFolder },
      autre: { label: 'Autres', icon: FiFolder }
    };

    const cats = [
      { id: 'all', label: 'Tous', count: projects.length, icon: FiLayers }
    ];

    Object.entries(categoryCounts).forEach(([id, count]) => {
      const config = categoryConfig[id] || { label: id, icon: FiFolder };
      cats.push({
        id,
        label: config.label,
        count,
        icon: config.icon
      });
    });

    return cats;
  }, [projects]);

  // ========== PROJETS FILTRÉS ==========
  const filteredProjects = useMemo(() => {
    if (!projects?.length) return [];

    let result = [...projects];

    if (activeCategory !== 'all') {
      result = result.filter(project => project.category === activeCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(project =>
        project.title?.toLowerCase().includes(term) ||
        project.shortDescription?.toLowerCase().includes(term) ||
        project.role?.toLowerCase().includes(term)
      );
    }

    // Tri : featured d'abord, puis par date
    result.sort((a, b) => {
      if (a.isFeatured && !b.isFeatured) return -1;
      if (!a.isFeatured && b.isFeatured) return 1;
      return (b.yearStart || 0) - (a.yearStart || 0);
    });

    return result;
  }, [projects, activeCategory, searchTerm]);

  // Stats pour le hero
  const stats = useMemo(() => {
    if (!projects?.length) return { total: 0, active: 0, featured: 0 };
    return {
      total: projects.length,
      active: projects.filter(p => p.status === 'active').length,
      featured: projects.filter(p => p.isFeatured).length
    };
  }, [projects]);

  const resetFilters = () => {
    setSearchTerm('');
    setActiveCategory('all');
  };

  if (loading) {
    return <LoadingScreen message="Chargement des projets..." />;
  }

  return (
    <div className="projects-page-wow">
      <Navigation />

      {/* ==================== HERO SECTION ==================== */}
      <section className="projects-hero-wow">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-mesh"></div>
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
          </div>
        </div>

        <div className="container">
          <motion.div
            className="hero-content-wow"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="hero-label">
              <FiFolder className="label-icon" />
              <span>Portfolio</span>
              <div className="label-line"></div>
            </div>

            <h1>
              Mes <span className="text-gradient">Réalisations</span>
            </h1>

            <p className="hero-description">
              Découvrez les projets sur lesquels j'ai travaillé, des initiatives 
              associatives aux collaborations avec des organisations internationales.
            </p>

            {/* Stats */}
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">{stats.total}</span>
                <span className="stat-label">Projets</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">{stats.active}</span>
                <span className="stat-label">En cours</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">{stats.featured}</span>
                <span className="stat-label">En vedette</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator">
          <div className="mouse">
            <div className="wheel"></div>
          </div>
          <span>Défiler</span>
        </div>
      </section>

      {/* ==================== FILTERS SECTION ==================== */}
      <section className="filters-section-wow">
        <div className="container">
          <div className="filters-wrapper-wow">
            {/* Search */}
            <div className="search-container">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher un projet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-btn"
                  onClick={() => setSearchTerm('')}
                  aria-label="Effacer"
                >
                  <FiX />
                </button>
              )}
            </div>

            {/* Categories Pills */}
            <div className="categories-scroll">
              <div className="categories-track">
                {categories.map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <button
                      key={category.id}
                      className={`category-pill-wow ${activeCategory === category.id ? 'active' : ''}`}
                      onClick={() => setActiveCategory(category.id)}
                    >
                      <IconComponent className="pill-icon" />
                      <span>{category.label}</span>
                      <span className="pill-count">{category.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROJECTS GRID ==================== */}
      <section className="projects-grid-section">
        <div className="container">
          {filteredProjects.length === 0 ? (
            <div className="no-results-wow">
              <div className="no-results-icon">
                <FiSearch />
              </div>
              <h3>Aucun projet trouvé</h3>
              <p>Essayez de modifier vos critères de recherche</p>
              <button className="reset-btn" onClick={resetFilters}>
                Réinitialiser les filtres
              </button>
            </div>
          ) : (
            <>
              {/* Animation uniquement sur le conteneur, pas sur chaque carte */}
              <motion.div 
                className="projects-masonry"
                initial={{ opacity: 0 }}
                animate={{ opacity: isPageReady ? 1 : 0 }}
                transition={{ duration: 0.4 }}
              >
                {filteredProjects.map((project, index) => (
                  <article
                    key={project.id}
                    className={`project-card-wow ${project.isFeatured ? 'featured' : ''}`}
                    style={{ 
                      animationDelay: `${Math.min(index * 0.05, 0.3)}s` 
                    }}
                  >
                    <Link to={`/projets/${project.slug}`} className="card-link">
                      {/* Image Container */}
                      <div className="card-visual">
                        <div className="card-image">
                          {project.mainImage ? (
                            <img 
                              src={project.mainImage} 
                              alt={project.title}
                              loading={index < 6 ? "eager" : "lazy"}
                            />
                          ) : (
                            <div className="card-placeholder">
                              <span>{project.title?.charAt(0) || 'P'}</span>
                            </div>
                          )}
                        </div>

                        {/* Overlay */}
                        <div className="card-overlay">
                          <div className="overlay-content">
                            <span className="view-label">
                              <FiArrowRight />
                              Voir le projet
                            </span>
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="card-badges">
                          {project.isFeatured && (
                            <span className="badge featured-badge">
                              <FiAward />
                              Vedette
                            </span>
                          )}
                          <span className={`badge status-badge ${project.status}`}>
                            {project.status === 'active' ? 'En cours' : 'Terminé'}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="card-content">
                        <div className="card-category">
                          {project.categoryLabel || project.category || 'Projet'}
                        </div>

                        <h3 className="card-title">{project.title}</h3>

                        <p className="card-excerpt">
                          {project.shortDescription || 'Découvrez ce projet passionnant.'}
                        </p>

                        <div className="card-meta">
                          <span className="meta-role">{project.role || 'Contributeur'}</span>
                          <span className="meta-date">
                            <FiCalendar />
                            {project.yearStart}{project.yearEnd ? ` - ${project.yearEnd}` : ' - Présent'}
                          </span>
                        </div>

                        <div className="card-action">
                          <span className="action-text">Découvrir</span>
                          <FiArrowRight className="action-arrow" />
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </motion.div>

              {/* Results count */}
              <div className="results-count-wow">
                <span className="count-number">{filteredProjects.length}</span>
                <span className="count-text">
                  projet{filteredProjects.length > 1 ? 's' : ''} 
                  {activeCategory !== 'all' ? ` dans ${categories.find(c => c.id === activeCategory)?.label}` : ''}
                </span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ==================== CTA SECTION ==================== */}
      <section className="cta-section-wow">
        <div className="container">
          <div className="cta-card-wow">
            <div className="cta-bg">
              <div className="cta-gradient"></div>
              <div className="cta-pattern"></div>
            </div>

            <div className="cta-content">
              <h2>Vous avez un projet en tête ?</h2>
              <p>
                Je suis toujours ouvert à de nouvelles collaborations et opportunités.
                Discutons de votre projet !
              </p>
              <Link to="/a-propos#contact" className="cta-button">
                <span>Me contacter</span>
                <FiArrowRight />
              </Link>
            </div>

            {/* Decorative elements */}
            <div className="cta-decor">
              <div className="decor-circle decor-1"></div>
              <div className="decor-circle decor-2"></div>
              <div className="decor-circle decor-3"></div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Projects;